import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { 
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Fade,
  Zoom,
  Button,
  Backdrop,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FeedbackIcon from '@mui/icons-material/Feedback';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

// Configure the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// נתוני הבנקים (דוגמה) - ניתן להתאים ללוגואים/הנחיות אמיתיות
const banksData = [
  {
    id: "hapoalim",
    name: "בנק הפועלים",
    logo: "/images/banks_logo/poalim_logo.png",
    pdfUrl: "/pdfs/siluk/poalim_siluk.pdf",
    color: "#e8112d"
  },
  {
    id: "leumi",
    name: "בנק לאומי",
    logo: "/images/banks_logo/leumi_logo.png",
    pdfUrl: "/pdfs/siluk/leumi_siluk.pdf",
    color: "#0252a2"
  },
  {
    id: "discount",
    name: "בנק דיסקונט",
    logo: "/images/banks_logo/diskont_logo.png",
    pdfUrl: "/pdfs/siluk/diskont_siluk.pdf",
    color: "#016661"
  },
  {
    id: "mizrahi",
    name: "בנק מזרחי טפחות",
    logo: "/images/banks_logo/mizrahi_logo.svg",
    pdfUrl: "/pdfs/siluk/mizrahi_siluk.pdf",
    color: "#973030"
  },
  {
    id: "fibi",
    name: "הבינלאומי הראשון",
    logo: "/images/banks_logo/benleumi_logo.svg",
    pdfUrl: "/pdfs/siluk/benleumi_siluk.pdf",
    color: "#5a667a"
  },
  {
    id: "markentil",
    name: "בנק מרכנתיל",
    logo: "/images/banks_logo/markantil_logo.png",
    pdfUrl: "/pdfs/siluk/markntil_siluk.pdf",
    color: "#8d0f0f"
  }
];

const HowToDownload = () => {
  const [selectedBank, setSelectedBank] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pdfWidth = isMobile ? window.innerWidth * 0.9 : 600;

  // פונקציה לסגירת המודאל
  const closeModal = () => {
    setSelectedBank(null);
    setPageNumber(1);
    setScale(1);
    setPdfLoading(true);
    setPdfError(false);
  };

  const handleDownload = async (pdfUrl, bankName) => {
    setIsDownloading(true);
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `הוראות_הורדה_${bankName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };
  
  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPdfLoading(false);
    setPdfError(true);
  };
  
  const changePage = (offset) => {
    setPageNumber(prevPageNumber => {
      const newPageNumber = prevPageNumber + offset;
      return Math.min(Math.max(1, newPageNumber), numPages);
    });
  };
  
  const zoomIn = () => setScale(prevScale => Math.min(prevScale + 0.2, 2.5));
  const zoomOut = () => setScale(prevScale => Math.max(prevScale - 0.2, 0.6));

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && selectedBank) {
        closeModal();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [selectedBank]);

  return (
    <PageContainer maxWidth="lg">
      <Paper elevation={0} sx={{ 
        py: 5, 
        px: { xs: 2, md: 5 },
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
        background: 'linear-gradient(to bottom, #fcfbfa, #ffffff)',
        border: '1px solid #f0f0f0'
      }}>
        <Fade in={true} timeout={600}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              <HelpOutlineIcon sx={{ color: '#896E3B', fontSize: 36, mr: 1.5 }} />
              <Typography variant="h4" component="h1" fontWeight={600} textAlign="center" color="#2c3e50">
                איך להוריד דו"ח יתרות לסילוק?
              </Typography>
            </Box>
            
            <Box sx={{ maxWidth: '700px', mx: 'auto', mb: 5 }}>
              <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 1 }}>
                בחרו את הבנק שלכם כדי לקבל הסבר מפורט כיצד להוריד את דו"ח היתרות לסילוק מאתר הבנק
              </Typography>
              
              <InfoBox>
                <InfoIcon sx={{ color: '#896E3B', fontSize: 20, mr: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  דו"ח יתרות לסילוק הוא מסמך רשמי המפרט את יתרת המשכנתא, תנאי ההלוואה ומועדי התשלום. הוא חיוני לבדיקת אפשרות למחזור משכנתא או שיפור תנאים.
                </Typography>
              </InfoBox>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {banksData.map((bank) => (
                <Grid item xs={6} sm={4} md={4} key={bank.id}>
                  <Zoom in={true} style={{ transitionDelay: `${banksData.indexOf(bank) * 100}ms` }}>
                    <BankCard 
                      onClick={() => setSelectedBank(bank)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      bankColor={bank.color}
                    >
                      <LogoContainer>
                        <BankLogo src={bank.logo} alt={bank.name} />
                      </LogoContainer>
                      <BankName>{bank.name}</BankName>
                    </BankCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
            
            <FeedbackContainer>
              <FeedbackIcon sx={{ fontSize: 18, mr: 1, color: '#896E3B' }} />
              <Typography variant="body2" color="text.secondary">
                לא מוצאים את הבנק שלכם או נתקלתם בבעיה? <FeedbackButton as="a" href="/contact">צרו קשר לעזרה</FeedbackButton>
              </Typography>
            </FeedbackContainer>
          </Box>
        </Fade>
      </Paper>

      {/* Modal for PDF instructions */}
      <Backdrop
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)' 
        }}
        open={selectedBank !== null}
        onClick={closeModal}
      >
        {selectedBank && (
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader bankColor={selectedBank.color}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src={selectedBank.logo} 
                  alt={selectedBank.name} 
                  style={{ 
                    width: '30px', 
                    height: '30px', 
                    marginLeft: '10px',
                    objectFit: 'contain' 
                  }} 
                />
                <Typography variant="h6" fontWeight={600} color="white">
                  הורדת דו"ח יתרות מ{selectedBank.name}
                </Typography>
              </Box>
              <CloseButton onClick={closeModal}>
                <CloseIcon fontSize="small" />
              </CloseButton>
            </ModalHeader>
            
            <ModalContent>
              <PDFControls>
                {numPages > 1 && (
                  <PageControls>
                    <ControlButton 
                      onClick={() => changePage(-1)} 
                      disabled={pageNumber <= 1}
                    >
                      הקודם
                    </ControlButton>
                    <PageInfo>
                      עמוד {pageNumber} מתוך {numPages}
                    </PageInfo>
                    <ControlButton 
                      onClick={() => changePage(1)} 
                      disabled={pageNumber >= numPages}
                    >
                      הבא
                    </ControlButton>
                  </PageControls>
                )}
                
                <ZoomControls>
                  <IconButton onClick={zoomOut} disabled={scale <= 0.6}>
                    <ZoomOutIcon />
                  </IconButton>
                  <Typography variant="body2">{Math.round(scale * 100)}%</Typography>
                  <IconButton onClick={zoomIn} disabled={scale >= 2.5}>
                    <ZoomInIcon />
                  </IconButton>
                </ZoomControls>
              </PDFControls>
              
              <PDFContainer>
                {pdfLoading && (
                  <LoadingContainer>
                    <Skeleton variant="rectangular" width={pdfWidth} height={pdfWidth * 1.4} />
                    <Typography variant="body2" sx={{ mt: 2 }}>טוען מסמך...</Typography>
                  </LoadingContainer>
                )}
                
                {pdfError && (
                  <ErrorContainer>
                    <Typography variant="body1" color="error">
                      שגיאה בטעינת המסמך
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      לא ניתן לטעון את ההוראות. אנא נסו להוריד את הקובץ במקום.
                    </Typography>
                  </ErrorContainer>
                )}
                
                <Document
                  file={selectedBank.pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={null}
                  error={null}
                >
                  <Page 
                    pageNumber={pageNumber} 
                    width={pdfWidth * scale}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </Document>
              </PDFContainer>
              
              <ActionButtons>
                <SecondaryButton
                  startIcon={<ArrowBackIcon />}
                  onClick={closeModal}
                >
                  חזרה לרשימת הבנקים
                </SecondaryButton>
                
                <PrimaryButton
                  startIcon={isDownloading ? null : <DownloadIcon />}
                  onClick={() => handleDownload(selectedBank.pdfUrl, selectedBank.name)}
                  disabled={isDownloading}
                  bankColor={selectedBank.color}
                >
                  {isDownloading ? (
                    <>
                      <LoadingSpinner />
                      <span style={{ marginLeft: '8px' }}>מוריד...</span>
                    </>
                  ) : (
                    "הורד PDF"
                  )}
                </PrimaryButton>
              </ActionButtons>
            </ModalContent>
          </ModalContainer>
        )}
      </Backdrop>
    </PageContainer>
  );
};

export default HowToDownload;

// ------------------- סגנונות (Styled Components) -------------------

const PageContainer = styled(Container)`
  margin: 40px auto;
  padding: 0 16px;
  font-family: "Alef", sans-serif;
  direction: rtl;
`;

const BankCard = styled(Paper)`
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 180px;
  border-radius: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: ${props => props.bankColor || '#896E3B'};
    opacity: 0.7;
    transition: height 0.3s ease;
  }
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
    
    &::before {
      height: 8px;
      opacity: 1;
    }
  }
  
  @media (max-width: 600px) {
    height: 140px;
    padding: 15px 10px;
  }
`;

const LogoContainer = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  
  @media (max-width: 600px) {
    width: 80px;
    height: 80px;
  }
`;

const BankLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const BankName = styled(Typography)`
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  font-size: 1rem;
  
  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const InfoBox = styled(Box)`
  display: flex;
  background-color: rgba(201, 177, 140, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin: 20px 0;
  border: 1px solid rgba(201, 177, 140, 0.2);
`;

const FeedbackContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 30px;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const FeedbackButton = styled.span`
  color: #896E3B;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  margin-right: 4px;
  
  &:hover {
    color: #b38d4d;
  }
  
  /* Support for when used as a link */
  display: inline-block;
  &[href] {
    text-decoration: underline;
  }
`;

// Modal Components
const ModalContainer = styled(Paper)`
  width: 95%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ModalHeader = styled(Box)`
  background-color: ${props => props.bankColor || '#896E3B'};
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ModalContent = styled(Box)`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const PDFContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
  flex: 1;
  margin: 10px 0;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
  position: relative;
  min-height: 400px;
  
  & .react-pdf__Page {
    margin: 0 auto;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    background-color: white;
  }
  
  & .react-pdf__Page__canvas {
    border-radius: 4px;
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const ErrorContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1;
  padding: 20px;
  text-align: center;
`;

const PDFControls = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 8px 8px 0 0;
  border: 1px solid #eee;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PageControls = styled(Box)`
  display: flex;
  align-items: center;
`;

const ZoomControls = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlButton = styled.button`
  background-color: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 5px 10px;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: #f0f0f0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  background-color: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: #f0f0f0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled(Typography)`
  margin: 0 10px;
  font-size: 0.875rem;
  color: #555;
`;

const ActionButtons = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    gap: 10px;
    
    & > button {
      width: 100%;
    }
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.bankColor || '#896E3B'};
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background-color: ${props => props.bankColor ? `${props.bankColor}dd` : '#735a31'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: #666;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  border: 1px solid #ddd;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

